import { useState } from "react";
import { Card, Flex, Tag, Button, Typography } from "antd";
import { DateField } from "@refinedev/antd";
import { useGo, useTranslate } from "@refinedev/core";
import {
  CheckCircleIcon,
  XCircleIcon,
  CrownIcon,
  RocketLaunchIcon,
  GiftIcon,
} from "@phosphor-icons/react";
import styled from "styled-components";
import dayjs from "dayjs";
import { OrganizationSubscription } from "types";
import {
  isTrialSubscription,
  isActiveSubscription,
} from "pages/media/projects/helpers/helper";
import { QuotaFeatures, FeatureFlags } from "@/constants";
import { useFeatureFlag } from "hooks/useFeature/";
import PricingModal from "./PricingModal";
import { useAnalytics } from "hooks/useAnalytics/";
import { AnalyticsEvents } from "types/analytics/";
import { radius } from "@libertify/ui";

const { Text, Title } = Typography;

const StyledCard = styled(Card)<{ $borderColor?: string }>`
  border-radius: ${radius.box}px;
  border: 1px solid ${({ $borderColor }) => $borderColor || "transparent"};

  .ant-card-body {
    padding: 32px;
  }
`;

const UpgradeBox = styled.div<{ $bgColor?: string }>`
  background: ${({ $bgColor }) =>
    $bgColor || "linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)"};
  border-radius: ${radius.box}px;
  padding: 24px;
  margin-top: 24px;
`;

const StatBox = styled.div`
  background: ${({ theme }) => theme.colorBgContainer};
  border: 1px solid ${({ theme }) => theme.colorBorder};
  border-radius: ${radius.box}px;
  padding: 16px 20px;
  min-width: 160px;
`;

const FeatureItem = styled(Flex)`
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colorBorderSecondary};

  &:last-child {
    border-bottom: none;
  }
`;

const featureTranslationKeys: Record<string, string> = {
  [QuotaFeatures.ConcurrentMediaGeneration]: "concurrentGenerations",
  [QuotaFeatures.MediaProject]: "videoSeconds",
  [QuotaFeatures.ProjectPublication]: "publishedProjects",
  [QuotaFeatures.UserLicenseAssignment]: "teamMembers",
  [QuotaFeatures.Workspace]: "workspaces",
};

type SubscriptionStatus =
  | "active"
  | "expiring"
  | "expired"
  | "canceled"
  | "trial"
  | "free"
  | "none";

interface CurrentSubscriptionCardProps {
  subscription?: OrganizationSubscription;
}

const isFreeSubscription = (subscription: OrganizationSubscription) => {
  return subscription?.license_type === "Free";
};

const getSubscriptionStatus = (
  subscription?: OrganizationSubscription
): SubscriptionStatus => {
  if (!subscription) {
    return "none";
  }

  if (subscription.status === "canceled") {
    return "canceled";
  }

  const expiryDate =
    subscription?.subscribed_products?.[0]?.expiry || subscription?.expiry;

  if (isFreeSubscription(subscription)) {
    return "free";
  }

  if (isTrialSubscription(subscription)) {
    return "trial";
  }

  if (!isActiveSubscription(expiryDate)) {
    return "expired";
  }

  const daysUntilExpiry = dayjs(expiryDate).diff(dayjs(), "day");
  if (daysUntilExpiry <= 30) {
    return "expiring";
  }

  return "active";
};

const CurrentSubscriptionCard = ({
  subscription,
}: CurrentSubscriptionCardProps) => {
  const t = useTranslate();
  const go = useGo();
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const isNoIframe = useFeatureFlag(FeatureFlags.SubscriptionNoIframe);
  const { trackEvent } = useAnalytics();

  const status = getSubscriptionStatus(subscription);
  const isCustom = subscription?.license_type === "Custom";
  const startDate =
    subscription?.subscribed_products?.[0]?.subscribed_date ||
    subscription?.start_date;
  const expiryDate =
    subscription?.subscribed_products?.[0]?.expiry || subscription?.expiry;
  const planName =
    status === "none"
      ? t("pages.subscription.noSubscription")
      : subscription?.subscribed_products?.[0]?.product_name ||
        subscription?.license_name ||
        t("pages.subscription.trial");
  const statusConfig = {
    active: {
      color: "#52c41a",
      borderColor: undefined,
      tagColor: "success",
      label: t("pages.subscription.status.active"),
      icon: <CheckCircleIcon size={20} weight="bold" />,
      ctaText: t("pages.subscription.buttons.upgrade"),
      ctaType: "primary" as const,
      showTag: true,
    },
    expiring: {
      color: "#faad14",
      borderColor: "#faad14",
      tagColor: "warning",
      label: t("pages.subscription.status.active"),
      icon: <CheckCircleIcon size={20} weight="bold" />,
      ctaText: t("pages.subscription.buttons.renewNow"),
      ctaType: "primary" as const,
      showTag: true,
    },
    expired: {
      color: "#ff4d4f",
      borderColor: "#ff4d4f",
      tagColor: "error",
      label: t("pages.subscription.status.expired"),
      icon: <XCircleIcon size={20} weight="bold" />,
      ctaText: t("pages.subscription.buttons.subscribeNow"),
      ctaType: "primary" as const,
      showTag: true,
    },
    canceled: {
      color: "#8c8c8c",
      borderColor: "#8c8c8c",
      tagColor: "default",
      label: t("pages.subscription.status.canceled"),
      icon: <XCircleIcon size={20} weight="bold" />,
      ctaText: t("pages.subscription.buttons.subscribeNow"),
      ctaType: "primary" as const,
      showTag: true,
    },
    trial: {
      color: "#1890ff",
      borderColor: undefined,
      tagColor: "processing",
      label: t("pages.subscription.status.trialing"),
      icon: <CrownIcon size={20} weight="bold" />,
      ctaText: t("pages.subscription.buttons.upgrade"),
      ctaType: "primary" as const,
      showTag: true,
    },
    free: {
      color: "#1890ff",
      borderColor: undefined,
      tagColor: undefined,
      label: "",
      icon: null,
      ctaText: t("pages.subscription.buttons.upgrade"),
      ctaType: "primary" as const,
      showTag: false,
    },
    none: {
      color: "#8c8c8c",
      borderColor: undefined,
      tagColor: "default",
      label: t("pages.subscription.status.noSubscription"),
      icon: <XCircleIcon size={20} weight="bold" />,
      ctaText: t("pages.subscription.buttons.subscribeNow"),
      ctaType: "primary" as const,
      showTag: true,
    },
  };

  const config = statusConfig[status];

  const handleUpgrade = () => {
    trackEvent(AnalyticsEvents.UpgradeButtonClick, {
      location: "subscription_card",
    });
    if (isNoIframe) {
      go({ to: "/subscription/subscriptions" });
    } else {
      setIsPricingModalOpen(true);
    }
  };

  const handleManage = () => {
    if (!subscription?.id) return;
    go({ to: `/subscription/${subscription.id}` });
  };

  return (
    <StyledCard $borderColor={config.borderColor}>
      <Flex justify="space-between" align="flex-start">
        <Flex align="center" gap={16}>
          {status === "free" || status === "none" ? (
            <GiftIcon size={40} weight="duotone" color={config.color} />
          ) : (
            <CrownIcon size={40} weight="duotone" color={config.color} />
          )}
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {planName}
            </Title>
            {subscription?.license_type && (
              <Text type="secondary" style={{ fontSize: 15 }}>
                {subscription.license_type}
              </Text>
            )}
          </div>
        </Flex>
        {config.showTag && (
          <Tag
            color={config.tagColor}
            icon={config.icon}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 14,
              padding: "4px 12px",
            }}
          >
            {config.label}
          </Tag>
        )}
      </Flex>

      {status !== "free" && status !== "none" && (
        <Flex gap={20} style={{ marginTop: 28 }} wrap="wrap">
          <StatBox>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t("pages.subscription.startDate")}
            </Text>
            <div style={{ fontSize: 15, marginTop: 4 }}>
              <DateField value={startDate} format="MMM DD, YYYY" />
            </div>
          </StatBox>
          <StatBox>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {status === "canceled"
                ? t("pages.subscription.endDate")
                : t("pages.subscription.renewalDate")}
            </Text>
            <div style={{ fontSize: 15, marginTop: 4 }}>
              <DateField value={expiryDate} format="MMM DD, YYYY" />
            </div>
          </StatBox>
        </Flex>
      )}

      {subscription?.features && (
        <div style={{ marginTop: 24 }}>
          <Text
            type="secondary"
            style={{ fontSize: 14, marginBottom: 12, display: "block" }}
          >
            {t("pages.subscription.planIncludes")}
          </Text>
          {Object.values(subscription.features).map((feature) => {
            const isConcurrent =
              feature.feature === QuotaFeatures.ConcurrentMediaGeneration;
            const translationKey = featureTranslationKeys[feature.feature];
            const featureLabel = translationKey
              ? t(`pages.subscription.features.${translationKey}`)
              : feature.feature;

            return (
              <FeatureItem
                key={feature.feature}
                justify="space-between"
                align="center"
              >
                <Text style={{ fontSize: 15 }}>{featureLabel}</Text>
                <Text strong style={{ fontSize: 15 }}>
                  {isConcurrent
                    ? feature.quota_limit
                    : `${feature.usage} / ${feature.quota_limit}`}
                </Text>
              </FeatureItem>
            );
          })}
        </div>
      )}

      {!isCustom && (
        <UpgradeBox>
          <Flex align="center" gap={16}>
            <RocketLaunchIcon size={32} weight="duotone" color="#1890ff" />
            <div style={{ flex: 1 }}>
              <Text strong style={{ fontSize: 16 }}>
                {status === "free" || status === "trial" || status === "none"
                  ? t("pages.subscription.upgradeBox.title")
                  : t("pages.subscription.upgradeBox.titlePaid")}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: 14 }}>
                {status === "free" || status === "trial" || status === "none"
                  ? t("pages.subscription.upgradeBox.description")
                  : t("pages.subscription.upgradeBox.descriptionPaid")}
              </Text>
            </div>
            <Button
              type={config.ctaType}
              size="large"
              onClick={handleUpgrade}
              style={{
                borderRadius: radius.pill,
                fontSize: 15,
                height: 44,
                paddingInline: 24,
              }}
            >
              {config.ctaText}
            </Button>
          </Flex>
        </UpgradeBox>
      )}

      {!isCustom &&
        status !== "trial" &&
        status !== "free" &&
        status !== "none" &&
        status !== "canceled" && (
          <Flex gap={12} style={{ marginTop: 20 }}>
            <Button onClick={handleManage}>
              {t("pages.subscription.buttons.manageSubscription")}
            </Button>
          </Flex>
        )}

      <PricingModal
        open={isPricingModalOpen}
        onClose={() => setIsPricingModalOpen(false)}
      />
    </StyledCard>
  );
};

export default CurrentSubscriptionCard;
